import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SurveyReportService } from '../../services/survey-report.service';
import { ACTIVE_REPORT_STATUSES, SurveyReport } from '../../models/survey-report.model';
import { ModalSurveyReportProgressComponent } from '../modal-survey-report-progress/modal-survey-report-progress.component';

export interface SurveyReportsDialogResult {
  action: 'generate';
}

@Component({
  selector: 'app-modal-survey-reports',
  templateUrl: './modal-survey-reports.component.html',
  styleUrls: ['./modal-survey-reports.component.scss'],
  standalone: false,
})
export class ModalSurveyReportsComponent implements OnDestroy {
  reports: SurveyReport[] = [];
  loading = true;
  failed = false;
  private pollTimer: any;

  constructor(
    public dialogRef: MatDialogRef<ModalSurveyReportsComponent, SurveyReportsDialogResult>,
    private dialog: MatDialog,
    private surveyReportService: SurveyReportService,
    @Inject(MAT_DIALOG_DATA) public data: { surveyId: number | string },
  ) {
    this.load();
    this.pollTimer = setInterval(() => {
      if (this.activeReport) {
        this.load();
      }
    }, 4000);
  }

  ngOnDestroy(): void {
    clearInterval(this.pollTimer);
  }

  get activeReport(): SurveyReport | null {
    return this.reports.find((report) => this.isActive(report)) ?? null;
  }

  get history(): SurveyReport[] {
    const active = this.activeReport;
    return this.reports.filter((report) => report !== active);
  }

  isActive(report: SurveyReport): boolean {
    return ACTIVE_REPORT_STATUSES.includes(report.status);
  }

  progressPercent(report: SurveyReport): number {
    return report.progress_total > 0 ? Math.round((report.progress_done / report.progress_total) * 100) : 0;
  }

  duration(report: SurveyReport): string {
    if (!report.created_at || !report.completed_at) {
      return '–';
    }
    const seconds = Math.max(0, Math.round((new Date(report.completed_at).getTime() - new Date(report.created_at).getTime()) / 1000));
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
  }

  generate(): void {
    this.dialogRef.close({ action: 'generate' });
  }

  openDetails(report: SurveyReport, confirmCancel = false): void {
    this.surveyReportService.show(this.data.surveyId, report.id, (full) => {
      this.dialog
        .open(ModalSurveyReportProgressComponent, {
          data: { surveyId: this.data.surveyId, report: full ?? report, confirmCancel },
          panelClass: 'medium-dialog',
          width: '640px',
          maxWidth: '95vw',
        })
        .afterClosed()
        .subscribe(() => this.load());
    });
  }

  download(report: SurveyReport): void {
    if (report.status === 'COMPLETED') {
      this.surveyReportService.download(this.data.surveyId, report);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private load(): void {
    this.surveyReportService.history(this.data.surveyId, (reports) => {
      this.reports = reports;
      this.loading = false;
      this.failed = false;
    }, () => {
      this.loading = false;
      this.failed = true;
    });
  }
}
